// ACCOUNTING (2026-06-17) — dedicated top-nav peer (PM-approved). Splits the
// accounting surfaces out of Operations: Operations = intake/routing/processing
// (Bill Intake, Bill Review, Time); Accounting = approvals/AR/QBO/trust/funds/
// payroll/reconciliation. Reuses the SAME components Operations rendered — no
// logic/permission change, only where they live. Each surface keeps its own cap gate.
import { useState, useEffect } from "react";
import { can } from "../api";
import { useNavigation } from "../components/nav/NavigationContext";
import PaymentCoding from "../components/PaymentCoding";
import { ReimbursementIntakeQueue } from "../components/ReimbursementIntake";
import { FundsIntakeRequests } from "../components/FundsIntakeRequests";
import { InvoicePrepWorkspace } from "../components/InvoicePrep";
import TrustReconciliation from "../components/TrustReconciliation";
import GeneralTrust from "../components/GeneralTrust";
import AccountingBillQueue from "../components/AccountingBillQueue";
import QboVendorDrift from "../components/QboVendorDrift";

const ACC_LABEL = { billqueue: "Bill Queue", invprep: "Invoices / AR", coding: "Payment Coding", reimbintake: "Funds Intake",
  trustrecon: "Trust Reconciliation", generaltrust: "General Trust", qbovendors: "QBO Vendors", payroll: "Payroll", overview: "Overview" };

export default function Accounting({ openTime, onWorkChanged, onNavigateToClaim }) {
  const canInvoicing = can("manage_invoicing");
  const canCode = can("code_payments");
  const canIntake = can("manage_reimbursement_intake");
  const canPayroll = can("view_payroll") || can("manage_payroll");  // gated area (Chunk C adds the caps)
  const canBills = can("manage_bill_intake");
  const canPayees = can("manage_payees");
  const [tab, setTab] = useState(
    canBills ? "billqueue" : canInvoicing ? "invprep" : canCode ? "coding" : canIntake ? "reimbintake" : canPayroll ? "payroll" : "overview");
  const [fundsMode, setFundsMode] = useState("requests");
  const { setSubLabel } = useNavigation();
  useEffect(() => { setSubLabel(ACC_LABEL[tab] || null); }, [tab, setSubLabel]);

  return (
    <div className="page">
      <div style={{ display: "flex", gap: 8, marginBottom: 16, borderBottom: "1px solid var(--border)" }}>
        {[
          { id: "billqueue", label: "Bill Queue", show: canBills },
          { id: "invprep", label: "Invoices / AR", show: canInvoicing },
          { id: "coding", label: "Payment Coding", show: canCode },
          { id: "reimbintake", label: "Funds Intake", show: canIntake },
          { id: "trustrecon", label: "Trust Reconciliation", show: canIntake },
          { id: "generaltrust", label: "General Trust", show: canIntake },
          { id: "qbovendors", label: "QBO Vendors", show: canPayees },
          { id: "payroll", label: "Payroll", show: canPayroll },
          { id: "overview", label: "Overview", show: true },
        ].filter(t => t.show).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ padding: "8px 14px", border: "none", borderBottom: tab === t.id ? "2px solid var(--navy)" : "2px solid transparent",
                           background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600,
                           color: tab === t.id ? "var(--navy)" : "var(--text2)" }}>{t.label}</button>
        ))}
      </div>

      {tab === "billqueue" && canBills && <AccountingBillQueue onNavigateToClaim={onNavigateToClaim} />}
      {tab === "invprep" && canInvoicing && <InvoicePrepWorkspace onNavigateToClaim={onNavigateToClaim} />}
      {tab === "coding" && canCode && <PaymentCoding onNavigateToClaim={onNavigateToClaim} onWorkChanged={onWorkChanged} />}
      {tab === "reimbintake" && canIntake && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[["requests", "Requests"], ["receipts", "QBO Receipts"]].map(([id, lbl]) => (
              <button key={id} onClick={() => setFundsMode(id)}
                style={{ padding: "6px 14px", borderRadius: 16, border: "1px solid var(--border)", cursor: "pointer", fontSize: 12, fontWeight: 600,
                         background: fundsMode === id ? "var(--navy)" : "transparent", color: fundsMode === id ? "white" : "var(--text2)" }}>{lbl}</button>
            ))}
          </div>
          {fundsMode === "requests"
            ? <FundsIntakeRequests onNavigateToClaim={onNavigateToClaim} />
            : <ReimbursementIntakeQueue onNavigateToClaim={onNavigateToClaim} />}
        </div>
      )}
      {tab === "trustrecon" && canIntake && <TrustReconciliation />}
      {tab === "generaltrust" && canIntake && <GeneralTrust />}
      {tab === "qbovendors" && canPayees && <QboVendorDrift />}

      {tab === "payroll" && canPayroll && (
        <div className="card">
          <div className="card-header"><h3>Payroll</h3></div>
          <div className="card-body">
            <p style={{ color: "var(--text2)", fontSize: 13, lineHeight: 1.6 }}>
              Payroll calculations for HR/Accounting will live here — a gated area
              (VIEW_PAYROLL / MANAGE_PAYROLL) separate from claimant PHI. Foundation in place;
              the calculation engine + payroll documents are the next build.
            </p>
          </div>
        </div>
      )}

      {tab === "overview" && (
        <div className="card">
          <div className="card-header"><h3>Accounting</h3></div>
          <div className="card-body">
            <p style={{ color: "var(--text2)", fontSize: 13, lineHeight: 1.6 }}>
              The Accounting console hosts <strong>Invoices / AR</strong>, <strong>Payment Coding</strong>,
              <strong> Funds Intake</strong>, <strong>Trust Reconciliation</strong>, <strong>General Trust</strong>,
              and <strong>Payroll</strong> — the approvals, money-movement, and reconciliation surfaces.
              Bill intake &amp; routing remain in <strong>Operations</strong>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
